import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ModeEditOutlineOutlinedIcon from "@mui/icons-material/ModeEditOutlineOutlined";
import { Chip, IconButton } from "@mui/material";
import { useTranslation } from "react-i18next";
import { DATE_RANGE_FIELD_TYPE, SELECT_FIELD_TYPE, TEXT_FIELD_TYPE } from "../../helpers/constants";
import { useDialogBetter } from "../../hooks/useDialogBetter";
import i18n from "../../i18n";
import DateService from "../../services/dateService";
import {
    useGetTaskStatuses,
    useGetTaskSubjects,
    useGetTaskSubsubjects,
} from "../../services/taskService";
import { useLocalizedProperty } from "../../utils";
import { EditTaskDialog } from "./EditTaskDialog";
import { FinalizeTaskDialog } from "./FinalizeTaskDialog";
import { TaskStatusText } from "./TaskStatusText";

export const TODO_TASKS_TABLE_NAME = "todo_tasks_table";

export const useTodoTasksTableFieldsConfig = () => {
    const { t } = useTranslation();
    const { openDialog } = useDialogBetter();

    const getLocalizedProperty = useLocalizedProperty();

    const { data: statuses } = useGetTaskStatuses({
        select: (data) =>
            data.results.map((status) => ({
                value: status.status_name_eng,
                label: getLocalizedProperty(status, "status_name"),
            })),
    });

    const { data: subjects } = useGetTaskSubjects({
        select: (data) =>
            data.results.map((subject) => ({
                value: subject.name_eng,
                label: t(`tasks.subjects.${subject.name_eng}`),
            })),
    });

    const { data: subsubjects } = useGetTaskSubsubjects(undefined, {
        select: (data) =>
            data
                .map((subsubject) => ({
                    value: subsubject.name_eng,
                    label: t(`tasks.subsubjects.${subsubject.name_eng}`),
                }))
                .sort((a, b) => (a.label.toLowerCase() < b.label.toLowerCase() ? -1 : 1)),
    });

    const priorities = [
        { value: "LOW", label: i18n.t("tasks.priorities.LOW") },
        { value: "NORMAL", label: i18n.t("tasks.priorities.NORMAL") },
        { value: "HIGH", label: i18n.t("tasks.priorities.HIGH") },
    ];

    return [
        {
            name: "name",
            getValueCallback: (rowData) => rowData.name || "",
            label: i18n.t("tasks.todoSidebar.table.name"),
            filterType: TEXT_FIELD_TYPE,
            getFilterFieldConfigCallback: (filteringData) => undefined,
            sortable: true,
        },
        {
            name: "customer",
            getValueCallback: (rowData) => {
                if (!rowData.customer?.person) {
                    return "";
                }
                const { name, surname } = rowData.customer.person;
                return `${name || ""} ${surname || ""}`.trim();
            },
            label: i18n.t("tasks.todoSidebar.table.customerName"),
            filterType: TEXT_FIELD_TYPE,
            getFilterFieldConfigCallback: (filteringData) => undefined,
            sortable: true,
        },
        {
            name: "subject",
            getValueCallback: (rowData) =>
                i18n.t(`tasks.subjects.${rowData.subject?.name_eng}`) || "",
            label: i18n.t("tasks.todoSidebar.table.subject"),
            filterType: SELECT_FIELD_TYPE,
            getFilterFieldConfigCallback: (filteringData) => ({
                options: subjects,
                optionKey: "value",
                optionLabel: "label",
            }),
            fieldConfig: {
                optionKey: "value",
                optionLabel: "label",
                options: subjects,
            },
            sortable: true,
        },
        {
            name: "subsubject",
            getValueCallback: (rowData) =>
                i18n.t(`tasks.subsubjects.${rowData.subsubject?.name_eng}`) || "",
            label: i18n.t("tasks.todoSidebar.table.subsubject"),
            filterType: SELECT_FIELD_TYPE,
            getFilterFieldConfigCallback: (filteringData) => ({
                options: subsubjects,
                optionKey: "value",
                optionLabel: "label",
            }),
            fieldConfig: {
                optionKey: "value",
                optionLabel: "label",
                options: subsubjects,
            },
            sortable: true,
        },
        {
            name: "status",
            getValueCallback: (rowData) => {
                return <TaskStatusText status={rowData.status} />;
            },
            label: i18n.t("tasks.todoSidebar.table.status"),
            filterType: SELECT_FIELD_TYPE,
            sortable: true,
            getFilterFieldConfigCallback: (filteringData) => ({
                options: statuses,
                optionKey: "value",
                optionLabel: "label",
            }),
            fieldConfig: {
                optionKey: "value",
                optionLabel: "label",
                options: statuses,
            },
        },
        {
            name: "created",
            getValueCallback: (rowData) => {
                if (!rowData.created) {
                    return "";
                }
                return DateService.convertDatetimeFromBackendToFormatYYYYMMDD(rowData.created);
            },
            label: i18n.t("tasks.todoSidebar.table.createdAt"),
            filterType: DATE_RANGE_FIELD_TYPE,
            getFilterFieldConfigCallback: (filteringData) => ({
                style: { minWidth: "120px" },
            }),
            sortable: true,
        },
        {
            name: "priority",
            getValueCallback: (rowData) => {
                const priorityText = i18n.t(`tasks.priorities.${rowData.priority}`);
                return (
                    <Chip
                        label={priorityText}
                        size="small"
                        color={
                            rowData.priority === "HIGH"
                                ? "error"
                                : rowData.priority === "NORMAL"
                                  ? "primary"
                                  : "default"
                        }
                    />
                );
            },
            label: i18n.t("tasks.todoSidebar.table.priority"),
            filterType: SELECT_FIELD_TYPE,
            sortable: true,
            getFilterFieldConfigCallback: (filteringData) => ({
                options: priorities,
                optionKey: "value",
                optionLabel: "label",
            }),
            fieldConfig: {
                optionKey: "value",
                optionLabel: "label",
                options: priorities,
            },
        },
        {
            name: "note",
            getValueCallback: (rowData) => {
                const note = rowData.note || "";
                // Truncate long notes for table display
                return note.length > 50 ? `${note.substring(0, 50)}...` : note;
            },
            label: i18n.t("tasks.todoSidebar.table.note"),
            filterType: TEXT_FIELD_TYPE,
            getFilterFieldConfigCallback: (filteringData) => undefined,
            sortable: false,
        },
        {
            name: "edit",
            getValueCallback: (rowData) => (
                <IconButton
                    color="primary"
                    onClick={(e) => {
                        e.stopPropagation();
                        openDialog({
                            Component: EditTaskDialog,
                            componentProps: {
                                task: rowData,
                            },
                        });
                    }}
                    size="small"
                    disabled={rowData.status.status_name_eng === "Finalized"}
                >
                    <ModeEditOutlineOutlinedIcon fontSize="small" />
                </IconButton>
            ),
            label: i18n.t("tasks.todoSidebar.table.edit"),
            getFilterFieldConfigCallback: (filteringData) => undefined,
            sortable: false,
            excludedFromExport: true,
            allowChangeVisibility: false,
        },
        {
            name: "finalize",
            getValueCallback: (rowData) => (
                <IconButton
                    color="success"
                    onClick={(e) => {
                        e.stopPropagation();
                        openDialog({
                            Component: FinalizeTaskDialog,
                            componentProps: {
                                task: rowData,
                            },
                        });
                    }}
                    size="small"
                    disabled={rowData.status.status_name_eng === "Finalized"}
                >
                    <CheckCircleOutlineIcon fontSize="small" />
                </IconButton>
            ),
            label: i18n.t("tasks.todoSidebar.table.finalize"),
            getFilterFieldConfigCallback: (filteringData) => undefined,
            sortable: false,
            excludedFromExport: true,
            allowChangeVisibility: false,
        },
    ];
};
